/**
 * TypewriterQueue — 通用打字机队列类。
 *
 * 职责：以固定速率将外部推入的字符分批输出，与业务逻辑完全解耦。
 *
 * 使用方式：
 *   const q = new TypewriterQueue({
 *     rate: 35,                        // 字符/秒，默认 DEFAULT_TYPEWRITER_RATE
 *     onOutput: (chunk) => appendText(chunk), // 每次输出一个文本片段
 *     onDone: () => markDone(),         // 队列清空 + flush 已调用后触发
 *   });
 *
 *   q.enqueue('新增的文本片段');   // SSE onMessage 时调用
 *   q.flush();                     // SSE done 时调用，队列排完即触发 onDone
 *   q.drain();                     // 立即输出队列剩余内容
 *   q.destroy();                   // abort 时调用，立即停止定时器
 */

/** 默认打字机速率（字符/秒），调小可放慢输出节奏 */
export const DEFAULT_TYPEWRITER_RATE = 65;
const TYPEWRITER_TICK_INTERVAL = 16;

export class TypewriterQueue {
  private queue: string[] = [];
  private timerId: ReturnType<typeof setInterval> | null = null;
  private flushed = false;
  private done = false;
  private lastTickTime = 0;
  private outputCarry = 0;

  private readonly rate: number;
  private readonly onOutput: (chunk: string) => void;
  private readonly onDone: () => void;

  constructor(options: {
    /** 每秒输出字符数，默认 65 */
    rate?: number;
    /** 每输出一个文本片段时调用 */
    onOutput: (chunk: string) => void;
    /** 队列清空且 flush 已被调用后触发一次 */
    onDone: () => void;
  }) {
    this.rate = options.rate ?? DEFAULT_TYPEWRITER_RATE;
    this.onOutput = options.onOutput;
    this.onDone = options.onDone;
    this.start();
  }

  /** SSE 推送新数据时调用，将字符逐个入队 */
  enqueue(text: string): void {
    if (this.done) return;
    for (const ch of text) {
      this.queue.push(ch);
    }
  }

  /**
   * SSE 推送完毕时调用。
   * 队列内剩余字符仍会按速率输出完，输出完毕后触发 onDone。
   */
  flush(): void {
    this.flushed = true;
    // 若队列已空，直接触发 done
    if (this.queue.length === 0) {
      this.triggerDone();
    }
  }

  /** 立即输出队列内所有剩余字符 */
  drain(): void {
    if (this.done) return;
    this.outputAll();
    if (this.flushed) {
      this.triggerDone();
    }
  }

  /** abort 时调用，立即清空并停止 */
  destroy(): void {
    this.stop();
    this.queue = [];
    this.done = true;
  }

  private start(): void {
    this.lastTickTime = Date.now();
    this.timerId = setInterval(() => this.tick(), TYPEWRITER_TICK_INTERVAL);
  }

  private stop(): void {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private tick(): void {
    if (this.queue.length === 0) {
      // 队列空：若 flush 已调用则结束
      if (this.flushed) {
        this.triggerDone();
      }
      this.lastTickTime = Date.now();
      return;
    }

    const now = Date.now();
    const elapsed = Math.max(now - this.lastTickTime, 0);
    this.lastTickTime = now;
    const rawCount = (elapsed * this.rate) / 1000 + this.outputCarry;
    const count = Math.floor(rawCount);
    this.outputCarry = rawCount - count;
    if (count <= 0) return;
    this.outputNext(count);
  }

  private outputNext(count: number): void {
    const chunk = this.queue.splice(0, count).join('');
    if (chunk) {
      this.onOutput(chunk);
    }
  }

  private outputAll(): void {
    if (this.queue.length === 0) return;
    this.outputNext(this.queue.length);
  }

  private triggerDone(): void {
    if (this.done) return;
    this.done = true;
    this.stop();
    this.onDone();
  }
}
